/*
 * Message Editor detail — a FULL-PAGE detail that replaces the list while open (standing rule 12;
 * owner request 2026-10-12: legacy opens a detail view, not a popup). It is a bespoke page rather
 * than the generic SettingsDetailPage because it carries a nested "Translations" child grid
 * (Language + Translation, with its own add / edit / delete) that the flat shared page cannot
 * express. Same frame as the City / Division / Country / Department details: title + breadcrumb,
 * action bar top and bottom, confirm → audited useSaveFlow → "Saved successfully.". Only the small
 * New / Edit Translation box is still a popup, as legacy. (The file keeps its old name.)
 */
import { useEffect, useState } from 'react';
import '../../modules/SettingsDetailPage.css'; // full-page `.sd-*` detail frame
import './messageEditorDetail.css';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { guardedExit } from '../../../hooks/useUnsavedGuard';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ViewToggle } from '../../../components/ViewToggle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useSaveFlow } from '../../../components/confirm';
import { api } from '../../../api/client';
import {
  useMessageEditor, useMessageModules, useMessageBos, useSaveMessageEditor,
  useMessageTranslations, useSaveMessageTranslation, useDeleteMessageTranslation,
  ACTION_TYPES, GLOSSARY_LANGUAGES, errMessage, type GlossaryTranslation,
} from './systemMasters';

interface Props {
  id: string | null; onClose: () => void; onHome?: () => void;
  /** How an EXISTING message opens: 'view' = read-only with an Edit button (a row click); 'edit' =
   *  straight into the editable form. A new message always opens editable. */
  initialMode?: 'view' | 'edit';
}

interface FormState { message: string; messageKey: string; moduleId: string; boId: string; actionType: number; remark: string; }
const BLANK: FormState = { message: '', messageKey: '', moduleId: '', boId: '', actionType: 0, remark: '' };
const langName = (v: number) => GLOSSARY_LANGUAGES.find((l) => l.v === v)?.t ?? String(v);

/**
 * Message Editor detail form — LIMS MessageEditor_DetailView. MS ID auto-generated; Module / BO are cascading
 * DefaultSetting dropdowns and OPTIONAL (owner request 2026-10-15 — they were required); Action Type = radio group; Remark free text;
 * a Translations child grid (staged on a new message, committed on Save). Save / Save and Close / Save and New / Cancel.
 */
export function MessageEditorDetailDialog({ id, onClose, onHome, initialMode = 'edit' }: Props) {
  const notify = useNotify();
  const { confirm, alert } = useConfirm();
  const runSave = useSaveFlow();
  const save = useSaveMessageEditor();
  const [msgId, setMsgId] = useState<string | null>(id);   // current message id (set after a new one is created)
  const isNew = !msgId;
  // View → Edit (owner request 2026-10-12): a row click shows the record read-only; the Edit button
  // turns the boxes and the Save buttons on. A new message has nothing to view, so it is editable.
  const [mode, setMode] = useState<'view' | 'edit'>(id ? initialMode : 'edit');
  const viewOnly = mode === 'view' && !isNew;


  const [form, setForm] = useState<FormState>(BLANK);
  const [err, setErr] = useState('');
  const detail = useMessageEditor(msgId ?? undefined);
  useEffect(() => {
    if (detail.data) setForm({
      message: detail.data.message ?? '', messageKey: detail.data.messageKey ?? '',
      moduleId: detail.data.moduleId ?? '', boId: detail.data.boId ?? '',
      actionType: detail.data.actionType, remark: detail.data.remark ?? '',
    });
  }, [detail.data]);

  const { data: modules = [] } = useMessageModules();
  const { data: bos = [] } = useMessageBos(form.moduleId || undefined);
  const set = (patch: Partial<FormState>) => { setErr(''); setForm((f) => ({ ...f, ...patch })); };

  // ── Translations child (staged on a new message; committed on Save) ──
  const { data: translations = [] } = useMessageTranslations(msgId ?? undefined);
  const saveTr = useSaveMessageTranslation(msgId ?? '');
  const delTr = useDeleteMessageTranslation(msgId ?? '');
  const [trSel, setTrSel] = useState<Set<string | number>>(new Set());
  const [trEdit, setTrEdit] = useState<{ id?: string; language: number; translation: string } | null>(null);
  const [trErr, setTrErr] = useState('');
  const [staged, setStaged] = useState<GlossaryTranslation[]>([]);
  const isStaged = (tid: string | number) => String(tid).startsWith('tmp-');
  const staging = !msgId;
  const trRows = [...translations, ...staged];
  const selStaged = trSel.size === 1 && isStaged([...trSel][0]!);

  const doSave = (mode: 'stay' | 'close' | 'new') => {
    // Module and BO are optional (owner request 2026-10-15 — they used to be required); a blank one is saved as none.
    setErr('');
    const wasNew = !msgId;
    let newId = msgId;
    void runSave({
      save: async () => {
        const res = await save.mutateAsync({
          id: msgId ?? undefined, message: form.message.trim(), messageKey: form.messageKey.trim() || null,
          moduleId: form.moduleId || null, boId: form.boId || null, actionType: form.actionType, remark: form.remark.trim() || null,
        });
        newId = msgId ?? (res as { data?: { data?: { id?: string } } })?.data?.data?.id ?? null;
        if (wasNew && newId && staged.length) {
          for (const tr of staged) await api.post(`/api/v1/system/message-editor/${newId}/translations`, { language: tr.language, translation: tr.translation });
        }
      },
      successMessage: 'Saved successfully.',
      onSaved: () => {
        setStaged([]); setTrSel(new Set());
        if (mode === 'close') { onClose(); return; }
        if (mode === 'new') { setMsgId(null); setForm(BLANK); setMode('edit'); return; }   // Save and New → blank form
        if (wasNew && newId) setMsgId(newId);   // Save (stay) → switch to edit; translations now committed
      },
      onError: (e: unknown) => setErr(errMessage(e)),
    });
  };

  // Cancel / breadcrumb: ask before dropping unsaved edits (standing rule 15).
  const leave = () => { void guardedExit(onClose); };
  // Detail shortcuts through the shared hotkey layer (standing rule 14).
  useHotkeys(viewOnly ? [
    { combo: 'alt+e', label: 'Edit', group: 'Message Editor', order: 1, run: () => setMode('edit') },
    { combo: 'f2', label: 'Edit', group: 'Message Editor', order: 2, run: () => setMode('edit') },
    { combo: 'esc', label: 'Close', group: 'Message Editor', order: 3, allowInInput: false, run: onClose },
  ] : [
    { combo: 'mod+s', label: 'Save', group: 'Message Editor', order: 1, run: () => doSave('stay') },
    { combo: 'mod+shift+s', label: 'Save & New', group: 'Message Editor', order: 2, run: () => doSave('new') },
    { combo: 'mod+enter', label: 'Save & Close', group: 'Message Editor', order: 3, run: () => doSave('close') },
    { combo: 'esc', label: 'Cancel', group: 'Message Editor', order: 4, allowInInput: false, run: leave },
  ]);

  const openTrEdit = (v: { id?: string; language: number; translation: string }) => { setTrErr(''); setTrEdit(v); };
  const applyTr = async () => {
    if (!trEdit) return;
    const text = trEdit.translation.trim();
    if (!text) { setTrErr('Translation must not be empty.'); return; }
    if (trRows.some((t) => t.id !== trEdit.id && t.language === trEdit.language && (t.translation ?? '').trim() === text)) { setTrErr('This translation already exists.'); return; }
    setTrErr('');
    if (!trEdit.id) {
      if (staging) {   // new message → stage in memory, committed on Save
        const tmp: GlossaryTranslation = { id: `tmp-${crypto.randomUUID()}`, language: trEdit.language, translation: text };
        setStaged((s) => [...s, tmp]); setTrSel(new Set([tmp.id])); setTrEdit(null); return;
      }
      try { const res = await saveTr.mutateAsync({ language: trEdit.language, translation: text }); const nid = (res as { data?: { data?: { id?: string } } })?.data?.data?.id; if (nid) setTrSel(new Set([nid])); setTrEdit(null); }
      catch (e) { setTrErr(errMessage(e)); }
      return;
    }
    try { await saveTr.mutateAsync({ id: trEdit.id, language: trEdit.language, translation: text }); setTrSel(new Set([trEdit.id])); setTrEdit(null); }
    catch (e) { setTrErr(errMessage(e)); }
  };
  const delSelectedTr = async () => {
    if (trSel.size === 0) { notify.warning('Select a translation to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${trSel.size} translation${trSel.size === 1 ? '' : 's'}?` })) return;
    const ids = [...trSel].map(String);
    try {
      const stagedIds = ids.filter(isStaged);
      if (stagedIds.length) setStaged((s) => s.filter((t) => !stagedIds.includes(t.id)));
      for (const tid of ids.filter((i) => !isStaged(i))) await delTr.mutateAsync(tid);
      setTrSel(new Set());
      await alert({ title: 'Success', message: 'Deleted successfully.', tone: 'success' });
    } catch (e) { notify.error(errMessage(e)); }
  };
  const editSelectedTr = () => {
    const tid = [...trSel][0]; if (!tid || isStaged(tid)) return;
    const t = trRows.find((x) => x.id === tid); if (t) openTrEdit({ id: t.id, language: t.language, translation: t.translation ?? '' });
  };

  const trCols: AppColumn<GlossaryTranslation>[] = [
    { field: 'edit', header: '', sortable: false, filterable: false, width: 40, align: 'center',
      render: (t) => viewOnly
        ? <button className="pts-icon-btn edit" disabled title="Press Edit to change translations"><i className="ti ti-edit" /></button>
        : isStaged(t.id)
        ? <button className="pts-icon-btn edit" disabled title="Save the message to edit this translation"><i className="ti ti-edit" /></button>
        : <button className="pts-icon-btn edit" title="Edit" onClick={(e) => { e.stopPropagation(); openTrEdit({ id: t.id, language: t.language, translation: t.translation ?? '' }); }}><i className="ti ti-edit" /></button> },
    { field: 'language', header: 'Language', width: '40%', minWidth: 140, sortable: false, text: (r) => langName(r.language) },
    { field: 'translation', header: 'Translation', width: '60%', minWidth: 200, sortable: false, text: (r) => r.translation ?? '' },
  ];

  const recordName = isNew ? 'New Message' : (detail.data?.msid ?? 'Message');
  const actions = (bottom?: boolean) => (
    viewOnly ? (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        <button className="sd-abtn primary" onClick={() => setMode('edit')} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
        <button className="sd-abtn cancel" onClick={onClose} title={`Close (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Close</button>
      </div>
    ) : (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      <button className="sd-abtn primary" onClick={() => doSave('stay')} disabled={save.isPending} title={`Save (${formatCombo('mod+s')})`}><span className="tb-i">💾</span> Save</button>
      <button className="sd-abtn" onClick={() => doSave('close')} disabled={save.isPending} title={`Save and Close (${formatCombo('mod+enter')})`}><span className="tb-i">💾</span> Save and Close</button>
      <button className="sd-abtn" onClick={() => doSave('new')} disabled={save.isPending} title={`Save and New (${formatCombo('mod+shift+s')})`}><span className="tb-i">📄</span> Save and New</button>
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button className="sd-abtn cancel" onClick={leave} disabled={save.isPending} title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
    </div>
    )
  );

  return (
    <div className="bl-page mlv-host">
      {/* List/Detail switch (as City / Payment Terms Setup): "List View" goes back to the list, asking first when
          there are unsaved changes. */}
      <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
        <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(); }} />
      </div>
      <div className="bl-grid-area">
        <div className="sd-wrap">
          <div className="sd-head">
            <div>
              <h2>Message Editor – {recordName}</h2>
              <div className="sd-bc">
                <a onClick={() => void guardedExit(() => onHome?.())}>Home</a><i className="ti ti-chevron-right" />
                <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
                <a onClick={leave}>Message Editor</a><i className="ti ti-chevron-right" /><span>{recordName}</span>
              </div>
            </div>
          </div>

          {actions()}

        <div className="sd-body me-detail">
          {err && <div className="acc-err" style={{ margin: '0 0 8px' }}>{err}</div>}
          {/* The shared standard detail grid, ONE left-hand column (owner request 2026-10-12: "make all
              at left side"): caption beside its box, square 24px controls, fields in the legacy order. */}
          <div className="sd-grid sd-grid--single sd-grid--side sd-grid--square me-grid">
            <div className="sd-row">
              <label className="sd-label" htmlFor="me-msid">MS ID</label>
              <input id="me-msid" className="sd-input readonly" value={isNew ? '' : (detail.data?.msid ?? '')} placeholder="Autogenerate" readOnly tabIndex={-1} />
            </div>
            <div className="sd-row">
              <label className="sd-label" htmlFor="me-message">Message</label>
              <textarea id="me-message" className={`sd-input sd-input--tall${viewOnly ? ' readonly' : ''}`} readOnly={viewOnly} value={form.message} onChange={(e) => set({ message: e.target.value })} />
            </div>
            {/* Message Key is read-only, as legacy. Module and BO are EDITABLE here (owner request 2026-10-12;
                legacy keeps them read-only, which left a new message unsaveable): BO lists the forms of the
                chosen Module, so it is cleared when the Module changes and waits until one is picked. */}
            <div className="sd-row">
              <label className="sd-label" htmlFor="me-key">Message Key</label>
              <input id="me-key" className="sd-input readonly" value={form.messageKey} readOnly tabIndex={-1} />
            </div>
            <div className="sd-row">
              <label className="sd-label" htmlFor="me-module">Module</label>
              <select id="me-module" className={`sd-input${viewOnly ? ' readonly' : ''}`} disabled={viewOnly} value={form.moduleId} onChange={(e) => set({ moduleId: e.target.value, boId: '' })}>
                <option value="">N/A</option>
                {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div className="sd-row">
              <label className="sd-label" htmlFor="me-bo">BO</label>
              <select id="me-bo" className={`sd-input${form.moduleId && !viewOnly ? '' : ' readonly'}`} value={form.boId} disabled={viewOnly || !form.moduleId}
                title={viewOnly || form.moduleId ? undefined : 'Pick a Module first'} onChange={(e) => set({ boId: e.target.value })}>
                <option value="">N/A</option>
                {bos.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="sd-row">
              <label className="sd-label" id="me-action-lbl">Action Type</label>
              <div className="me-radios" role="radiogroup" aria-labelledby="me-action-lbl">
                {ACTION_TYPES.map((a) => (
                  <label key={a.v} className="me-radio">
                    <input type="radio" name="me-action" checked={form.actionType === a.v} disabled={viewOnly} onChange={() => set({ actionType: a.v })} />
                    {a.t}
                  </label>
                ))}
              </div>
            </div>
            <div className="sd-row">
              <label className="sd-label" htmlFor="me-remark">Remark</label>
              <textarea id="me-remark" className={`sd-input sd-input--tall${viewOnly ? ' readonly' : ''}`} readOnly={viewOnly} value={form.remark} onChange={(e) => set({ remark: e.target.value })} />
            </div>
          </div>

          <div className="gloss-tr">
            <div className="acc-sub-title">Translations</div>
            <div className="acc-sub-toolbar">
              <button className="ca-btn" onClick={() => openTrEdit({ language: 0, translation: '' })} disabled={viewOnly}><i className="ti ti-plus" /> New</button>
              <button className="ca-btn" onClick={editSelectedTr} disabled={viewOnly || trSel.size !== 1 || selStaged}><i className="ti ti-edit" /> Edit</button>
              <button className="ca-btn" onClick={() => void delSelectedTr()} disabled={viewOnly || trSel.size === 0}><i className="ti ti-trash" /> Delete</button>
              <ExportMenu triggerClassName="ca-btn" filename="MessageEditor-Translations" headers={['Language', 'Translation']}
                rows={trRows.map((t) => [langName(t.language), t.translation ?? ''])} />
            </div>
            <AppDataGrid<GlossaryTranslation> rows={trRows} columns={trCols} getRowId={(r) => r.id}
              selectable selectedIds={trSel} onSelectionChange={setTrSel}
              emptyText="No data to display." defaultPageSize={20} />
          </div>
        </div>

          {actions(true)}
        </div>
      </div>

      {trEdit && (
        <div className="acc-overlay" onMouseDown={() => setTrEdit(null)}>
          <div className="acc-dialog gloss-tr-edit acc-dialog--accent" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>{trEdit.id ? 'Edit Translation' : 'New Translation'}</span><i className="ti ti-x acc-dialog-x" onClick={() => setTrEdit(null)} /></div>
            <div className="acc-dialog-body">
              <Field label="Language" req tall>
                <div className="gloss-radio-group">
                  {GLOSSARY_LANGUAGES.map((l) => (
                    <label key={l.v} className="gloss-radio">
                      <input type="radio" name="me-tr-lang" checked={trEdit.language === l.v} onChange={() => { setTrErr(''); setTrEdit({ ...trEdit, language: l.v }); }} />
                      {l.t}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Translation" req><input className="acc-input" value={trEdit.translation} onChange={(e) => { setTrErr(''); setTrEdit({ ...trEdit, translation: e.target.value }); }} /></Field>
              {trErr && <div className="gloss-tr-err">{trErr}</div>}
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void applyTr()}><span className="tb-i">💾</span> OK</button>
              <button className="ca-btn" onClick={() => setTrEdit(null)}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, req, tall, children }: { label: string; req?: boolean; tall?: boolean; children: React.ReactNode }) {
  return (
    <div className={`acc-field-h${tall ? ' gloss-field-tall' : ''}`}>
      <label className={`acc-label${req ? ' gloss-req' : ''}`}>{label}{req && <span className="gloss-star">*</span>}</label>
      <div className="acc-fh-control">{children}</div>
    </div>
  );
}
